import { Loader, TableDataRequest, useIdFromRouteOrNull, Space, Card, BackButton, BasicDetailInfo } from '@fl/cmsch-fe-library';
import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { opt, Opt } from 'ts-opt';
import { RolePermission } from 'api/gen/RolePermission';
import { Content } from 'app/content';
import * as routerActions from 'app/router';
import { useOurTranslation } from 'app/translations';
import useCurrentUserHasAllPermissions from 'app/user/utils/use-current-user-has-all-permissions';
import { Ant } from 'common/ant';
import { RolePermissionsTable } from '../components/role-permissions-table';
import { SubordinateRoleAssignedTable } from '../components/role-subordinate-role-tables/assigned-table';
import { SubordinateRoleUnassignedTable } from '../components/role-subordinate-role-tables/unassigned-table';
import { RoleUnassignedPermissionsTable } from '../components/role-unassigned-permissions-table';
import { adminPermissions } from '../constants/permissions';
import { adminAction } from '../model';
import { simpleAdminSelector } from '../model/selector';
const rolesHeader = (name: string): ReactNode => <Space data-sentry-element="Space" data-sentry-component="rolesHeader" data-sentry-source-file="role-detail.tsx">
        <span>{name}</span>
    </Space>;
type PermissionDataRequest = Opt<TableDataRequest<RolePermission>>;

// eslint-disable-next-line max-lines-per-function
const RoleDetailBase: FC = () => {
  const dispatch = useDispatch();
  const id = useIdFromRouteOrNull();
  const roleIdString = useMemo(() => String(id), [id]);
  const canUpdateRole = useCurrentUserHasAllPermissions([adminPermissions.updateRole]);
  useEffect(() => {
    if (isNaN(Number(id))) {
      throw new Error(`Role detail id is not a number. ID: ${id}`);
    } else {
      dispatch(adminAction.getRole(Number(id)));
    }
    dispatch(adminAction.getAuthDiscriminatorOptions());
  }, [id, dispatch]);
  useEffect(() => () => {
    dispatch(adminAction.clearRole());
  }, [dispatch]);
  const {
    t,
    tCommon
  } = useOurTranslation('admin');
  const role = useSelector(simpleAdminSelector.roleDetail);
  const discriminators = useSelector(simpleAdminSelector.discriminators);
  const goBackToList = useCallback(() => {
    dispatch(routerActions.push('/administrace/role'));
  }, [dispatch]);
  const getRolePermissions = useCallback((roleId: string, tableDataRequest: PermissionDataRequest) => {
    dispatch(adminAction.getRolePermissions(Number(roleId), tableDataRequest));
  }, [dispatch]);
  const removePermissionFromRole = useCallback((roleId: string, permissionId: number) => {
    dispatch(adminAction.removePermissionFromRole(Number(roleId), permissionId));
  }, [dispatch]);
  const getRoleUnassignedPermissions = useCallback((roleId: string, tableDataRequest: PermissionDataRequest) => {
    dispatch(adminAction.getRoleUnassignedPermissions(Number(roleId), tableDataRequest));
  }, [dispatch]);
  const addPermissionToRole = useCallback((roleId: string, permissionId: number) => {
    dispatch(adminAction.addPermissionToRole(Number(roleId), permissionId));
  }, [dispatch]);
  if (role.isEmpty) {
    return <Content browserTabTitle={tCommon('loading')}>
                <Loader show size="large" text={tCommon('loading')} />
            </Content>;
  }
  const {
    name,
    discriminatorId,
    code,
    id: parentRoleId,
    isSubordinate
  } = role.orCrash(`Role is not available. Role ID: ${id}`);
  const discriminatorName = opt(discriminators.find(d => d.id === discriminatorId)).prop('name').orElse('');
  return <Content browserTabTitle={t('roles')} data-sentry-element="Content" data-sentry-component="RoleDetailBase" data-sentry-source-file="role-detail.tsx">
            <Card testId="screenTitle" title={rolesHeader(name)} titleSize="medium" extra={<BackButton text={tCommon('backToList')} onClick={goBackToList} />} data-sentry-element="Card" data-sentry-source-file="role-detail.tsx">
                <BasicDetailInfo labels={[t('code'), t('discriminator')]} values={[code, discriminatorName]} data-sentry-element="BasicDetailInfo" data-sentry-source-file="role-detail.tsx" />
                <Ant.Tabs type="card" data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                    <Ant.Tabs.TabPane key="permissions" tab={t('permissions')} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                        <Ant.Row gutter={16} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                            <Ant.Col xs={24} lg={12} className="pb-2" data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                                <RolePermissionsTable roleId={roleIdString} getRolePermissions={getRolePermissions} removePermissionFromRole={removePermissionFromRole} canUpdateRole={canUpdateRole} data-sentry-element="RolePermissionsTable" data-sentry-source-file="role-detail.tsx" />
                            </Ant.Col>
                            <Ant.Col xs={24} lg={12} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                                <RoleUnassignedPermissionsTable roleId={roleIdString} getRoleUnassignedPermissions={getRoleUnassignedPermissions} addPermissionToRole={addPermissionToRole} canUpdateRole={canUpdateRole} data-sentry-element="RoleUnassignedPermissionsTable" data-sentry-source-file="role-detail.tsx" />
                            </Ant.Col>
                        </Ant.Row>
                    </Ant.Tabs.TabPane>
                    <Ant.Tabs.TabPane key="users" tab={<span title={isSubordinate ? t('subordinateRolesDisabled') : undefined}>
                                {t('subordinateRoles')}
                            </span>} disabled={isSubordinate} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                        <Ant.Row gutter={16} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                            <Ant.Col xs={24} lg={12} className="pb-2" data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                                <SubordinateRoleAssignedTable parentRoleId={parentRoleId} canUpdateRole={canUpdateRole} data-sentry-element="SubordinateRoleAssignedTable" data-sentry-source-file="role-detail.tsx" />
                            </Ant.Col>
                            <Ant.Col xs={24} lg={12} data-sentry-element="unknown" data-sentry-source-file="role-detail.tsx">
                                <SubordinateRoleUnassignedTable parentRoleId={parentRoleId} canUpdateRole={canUpdateRole} data-sentry-element="SubordinateRoleUnassignedTable" data-sentry-source-file="role-detail.tsx" />
                            </Ant.Col>
                        </Ant.Row>
                    </Ant.Tabs.TabPane>
                </Ant.Tabs>
            </Card>
        </Content>;
};

/** Admin role detail screen. */
export const RoleDetail = memo(RoleDetailBase);